import React, { useEffect, useRef, useState } from 'react';
import { useCommon } from "../context/CommonContext";
import { useNavigate } from "react-router-dom";
import { useLanguage } from '../context/LanguageProvider';

const IATTestv2 = () => {

    /**
     * ──────────────────────────────────────────────
     *  Context and Navigation
     * ──────────────────────────────────────────────
     */
    const {
        attributes,              // Configurable attributes/word lists for the IAT
        currentMemoryIndex,      // Pointer to current memory index
        memories,                // Memory data
        iatTestReaction,         // Shared reaction results array (mutable ref)
        intermissionText,        // Text for intermission between games
        kioskTimeoutText,        // Text when kiosk inactivity timeout occurs
        prompts,                 // Text prompts for the user
        questionID               // IDs for current question context
    } = useCommon();

    const { translation } = useLanguage(); // Translation hook for multilingual labels
    const navigate = useNavigate();

    /**
     * ──────────────────────────────────────────────
     *  Word List Setup
     * ──────────────────────────────────────────────
     * If no attributes loaded, use default words.
     * WORDS array is the pool for a game round.
     */
    const WORDS = attributes[0].length === 0
        ? ["Default-1", "Default-2", "Default-3", "Default-4", "Default-5", "Default-6", "Default-7", "Default-8", "Default-9"]
        : attributes[0];

    /**
     * ──────────────────────────────────────────────
     *  Core Game Configuration
     * ──────────────────────────────────────────────
     */
    const config = {
        minReactionTime: 150,                                       // ms: too-fast response threshold
        maxDisplayTime: 1000,                                       // ms: maximum time a word stays on screen without response
        delayBetweenWordsMin: 1000,                                 // ms: lower bound between word presentations
        delayBetweenWordsMax: 1500,                                 // ms: upper bound between word presentations
        noReactionTime: 1000,                                       // ms: recorded when no reaction
        inactivityTime: 240000,                                     // ms: inactivity timeout (4 minutes)
        breakTime: 10000,                                           // ms: break duration between games
        gameDelay: 1500,                                            // ms: delay before starting after break
        maxMissedResponses: Math.floor(WORDS.length / 2),           // allowable missed responses before showing errorOne
        noofGames: 2,                                               // how many rounds/games to play
        AditionalInitialWordDelay: 1500                             // initial extra delay before first word
    };

    console.log("--------------component rendered-------------");

    /**
     * ──────────────────────────────────────────────
     *  React State (UI-driven values)
     * ──────────────────────────────────────────────
     */
    const [currentWord, setCurrentWord] = useState("");   // The word currently displayed
    const [isGameOver, setIsGameOver] = useState(false);  // Flag when a game has ended
    const [isInactive, setIsInactive] = useState(false);  // Show kiosk timeout overlay

    // Error UI flags for different types of incorrect responses:
    const [errorOneMessage, setErrorOneMessage] = useState(false);   // Too many misses
    const [errorTwoMessage, setErrorTwoMessage] = useState(false);   // Reaction too fast
    const [errorThreeMessage, setErrorThreeMessage] = useState(false); // Spacebar pressed without a word on screen

    // Break messaging & "Press space to continue" states
    const [isBreak, setIsBreak] = useState(false);
    const [isBreak1, setIsBreak1] = useState(false);
    const [canContinue, setCanContinue] = useState(false);     // Can continue from first break
    const [canContinue3, setCanContinue3] = useState(false);   // Can continue from second break

    // Refs for "can continue" states to persist across renders
    const canContinue2 = useRef(false);
    const canContinue4 = useRef(false);

    /**
     * ──────────────────────────────────────────────
     *  Mutable Refs (non-UI state)
     * ──────────────────────────────────────────────
     */
    const finalWordListRef = useRef([]);           // Shuffled and processed list for the game
    const wordIndexRef = useRef(0);                // Current index in finalWordList
    const startTimeRef = useRef(null);             // Timestamp when a word was displayed
    const timerRef = useRef(null);                 // Timeout handle for word display expiry
    const displayNextWordTimerRef = useRef(null);  // Timeout for delay between words
    const inactivityTimerRef = useRef(null);       // Timeout handle for global inactivity
    const isSpacePressedRef = useRef(false);       // Block multiple spacebar presses
    const isPausedRef = useRef(false);             // Game paused due to error condition
    const missedResponsesRef = useRef(0);          // Count of consecutive misses
    const isTransitioningRef = useRef(false);      // True when between words / showing delay
    const gamesPlayed = useRef(0);                 // Tracks how many games have been completed
    const isInitializedRef = useRef(false);        // Ensures initialisation only runs once
    const gameEnded = useRef(false);               // Marks if game is fully ended

    /**
     * ──────────────────────────────────────────────
     *  Helper Functions
     * ──────────────────────────────────────────────
     */

    // Shuffle array elements in place
    const shuffleArray = (array) => {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    };

    // Builds the final display list, injecting "burnoutWords" early on
    const createFinalWordList = () => {
        const shuffledWords = shuffleArray([...WORDS]);
        const burnoutCount = 2; // number of burnout/prime words to prepend
        const randomIndex = Math.random() < 0.5 ? 3 : 4; // starting index in shuffled list
        const slicedWords = shuffledWords.slice(randomIndex);
        const burnoutWords = getRandomWords(slicedWords, burnoutCount);
        const finalList = [...burnoutWords, ...shuffledWords];
        finalWordListRef.current = finalList;
        console.log(`finalword list - ${finalWordListRef.current}`);
    };

    // Picks N random unique words from given array
    const getRandomWords = (array, count) => {
        const result = [];
        const seenIndices = new Set();

        while (result.length < count) {
            const randomIndex = Math.floor(Math.random() * array.length);
            if (!seenIndices.has(randomIndex)) {
                seenIndices.add(randomIndex);
                result.push(array[randomIndex]);
            }
        }

        return result;
    };

    /**
     * ──────────────────────────────────────────────
     *  useEffect — Component Mount / Initial Game Start
     * ──────────────────────────────────────────────
     */
    useEffect(() => {
        console.log("--------------useEffect called-------------");
        let timeoutId;

        // Sets the first word after random + additional delay
        const setCurrentWordWithDelay = () => {
            return new Promise((resolve) => {
                const initialDelay =
                    (Math.random() * (config.delayBetweenWordsMax - config.delayBetweenWordsMin)
                        + config.delayBetweenWordsMin
                        + config.AditionalInitialWordDelay);
                timeoutId = setTimeout(() => {
                    setCurrentWord(finalWordListRef.current[0]);
                    resolve();
                }, initialDelay);
            });
        };

        const initializeGame = async () => {
            if (!isInitializedRef.current) {
                createFinalWordList();
                await setCurrentWordWithDelay();
                isInitializedRef.current = true;
            }

            missedResponsesRef.current = 0;

            // Register interaction listeners
            window.addEventListener('keydown', handleKeyPress);
            window.addEventListener('mousemove', resetInactivityTimer);
            resetInactivityTimer();

            console.log('Game started');
            startTimeRef.current = performance.now(); // mark start
                        console.log(`Displaying word: ${finalWordListRef.current[wordIndexRef.current]}`);

            // Schedule auto-miss timeout for first word
            timerRef.current = setTimeout(() => {
                console.log(`No reaction for word: ${finalWordListRef.current[wordIndexRef.current]}`);
                isTransitioningRef.current = true;
                missedResponsesRef.current += 1;
                // Record miss reaction
                iatTestReaction.current.push([
                    finalWordListRef.current[wordIndexRef.current],
                    config.noReactionTime,
                    memories[currentMemoryIndex.current],
                    gamesPlayed.current,
                    wordIndexRef.current,
                    false, // no reaction
                    currentMemoryIndex.current + 1,
                    questionID[0],
                ]);

                const randomDelay = (Math.random() *
                    (config.delayBetweenWordsMax - config.delayBetweenWordsMin)
                    + config.delayBetweenWordsMin);
                                    console.log(`random delay ${randomDelay}`);

                setCurrentWord("");
                displayNextWordTimerRef.current = setTimeout(displayNextWord, randomDelay);
            }, config.maxDisplayTime);
        };

        initializeGame();

        // Cleanup on unmount
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
            window.removeEventListener('mousemove', resetInactivityTimer);
            clearTimeout(timerRef.current);
            clearTimeout(inactivityTimerRef.current);
            clearTimeout(timeoutId);
        };

    }, []);

    /**
     * ──────────────────────────────────────────────
     *  Timer Reset for Inactivity Overlay
     * ──────────────────────────────────────────────
     */
    const resetInactivityTimer = () => {
        setIsInactive(false);
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = setTimeout(() => {
            setIsInactive(true); // Show inactivity screen
        }, config.inactivityTime);
    };

    /**
     * ──────────────────────────────────────────────
     *  Main Word Progression Logic
     * ──────────────────────────────────────────────
     * Called after a correct/incorrect response (or miss) and its delay.
     */
    const displayNextWord = () => {
        console.log("--------------displayNextWord called-------------");

        const nextIndex = wordIndexRef.current + 1;

        if (nextIndex >= finalWordListRef.current.length) {
            // Game ends — either break or exit
            setIsGameOver(true);
            gameEnded.current = true;
            console.log('Game Over. Reaction Times:', iatTestReaction.current);
            handleGameOver();
            return;
        }

        wordIndexRef.current = nextIndex;
        setCurrentWord(finalWordListRef.current[nextIndex]);
        isTransitioningRef.current = false;
        isSpacePressedRef.current = false;
        startTimeRef.current = performance.now();

                console.log(`Displaying word: ${finalWordListRef.current[wordIndexRef.current]}`);

        // Schedule timeout for missed reaction
        timerRef.current = setTimeout(() => {
            console.log(`No reaction for word: ${finalWordListRef.current[wordIndexRef.current]}`);
            setCurrentWord("");
            missedResponsesRef.current += 1;

            if (missedResponsesRef.current > config.maxMissedResponses) {
                // Pause game, show errorOne
                const currentWord = finalWordListRef.current[wordIndexRef.current];
                iatTestReaction.current.push([
                    currentWord,
                    config.noReactionTime,
                    memories[currentMemoryIndex.current],
                    gamesPlayed.current,
                    wordIndexRef.current,
                    false,
                    currentMemoryIndex.current + 1,
                    questionID[0],
                ]);
                isPausedRef.current = true;
                isSpacePressedRef.current = false;
                setErrorOneMessage(true);
            } else {
                // Record miss and continue
                isTransitioningRef.current = true;
                iatTestReaction.current.push([
                    finalWordListRef.current[wordIndexRef.current],
                    config.noReactionTime,
                    memories[currentMemoryIndex.current],
                    gamesPlayed.current,
                    wordIndexRef.current,
                    false,
                    currentMemoryIndex.current + 1,
                    questionID[0],
                ]);
                const randomDelay = (Math.random() * (config.delayBetweenWordsMax - config.delayBetweenWordsMin) +
                    config.delayBetweenWordsMin);
                    console.log(`random delay ${randomDelay}`);
                displayNextWordTimerRef.current = setTimeout(displayNextWord, randomDelay);
            }
        }, config.maxDisplayTime);
    };

    /**
     * ──────────────────────────────────────────────
     *  Game Over Handler
     * ──────────────────────────────────────────────
     * Controls transition between games and to the end screen.
     */
    const handleGameOver = () => {
        gamesPlayed.current += 1;
        if (gamesPlayed.current < config.noofGames) {
            // Show break before restarting
            setIsBreak(true);
            isTransitioningRef.current = true;
            gameEnded.current = true;
                        console.log("First game over. Starting break before the second game...");
            setTimeout(() => {
                                // console.log("Starting second game...");
                // resetGame();
                setCanContinue(true);
                canContinue2.current = true;
            }, config.breakTime);
        } else {
            // Completed all games
                        console.log("Second game over. Navigating to thank you page...");
            if (attributes.length > 1) {
                setIsGameOver(true);
                setIsBreak1(true);
                canContinue4.current = true;
                setTimeout(() => {
                    setCanContinue3(true);
                }, config.breakTime);
            } else {
                navigate('/IATTestThankYou');
            }
        }
    };

    /**
     * ──────────────────────────────────────────────
     *  Game Reset for Second Game
     * ──────────────────────────────────────────────
     */
    const resetGame = async () => {
                console.log("Resetting game for the next round...");
        setIsGameOver(false);
        const randomDelay = (Math.random() *
            (config.delayBetweenWordsMax - config.delayBetweenWordsMin) +
            config.delayBetweenWordsMin +
            config.AditionalInitialWordDelay);
        await new Promise((resolve) => setTimeout(resolve, randomDelay));
        isPausedRef.current = false;
        isSpacePressedRef.current = false;
        wordIndexRef.current = 0;
        missedResponsesRef.current = 0;
        createFinalWordList();
        isTransitioningRef.current = false;
        gameEnded.current = false;
        setCurrentWord(finalWordListRef.current[0]);
        startTimeRef.current = performance.now();

        // Schedule initial miss timeout
        timerRef.current = setTimeout(() => {
                        console.log(`No reaction for word: ${finalWordListRef.current[wordIndexRef.current]}`);
            isTransitioningRef.current = true;
            missedResponsesRef.current += 1;
            iatTestReaction.current.push([
                finalWordListRef.current[wordIndexRef.current],
                config.noReactionTime,
                memories[currentMemoryIndex.current],
                gamesPlayed.current,
                wordIndexRef.current,
                false,
                currentMemoryIndex.current + 1,
                questionID[0],
            ]);
            const randomDelay = (Math.random() *
                (config.delayBetweenWordsMax - config.delayBetweenWordsMin) +
                config.delayBetweenWordsMin);
                            console.log(`random delay ${randomDelay}`);
            setCurrentWord("");
            displayNextWordTimerRef.current = setTimeout(displayNextWord, randomDelay);
        }, config.maxDisplayTime);
    };

    /**
     * ──────────────────────────────────────────────
     *  Spacebar Handler — Core Interaction Logic
     * ──────────────────────────────────────────────
     */
    const handleKeyPress = (event) => {
        resetInactivityTimer();

        // Resume from first break
        if (event.code === 'Space' && canContinue2.current) {
            canContinue2.current = false;
            setCanContinue(false);
            setIsBreak(false);
            setTimeout(resetGame, config.gameDelay);
            return;
        }

        // Resume from second break to IATTestv2a
        if (event.code === 'Space' && canContinue4.current) {
            navigate('/IATTestv2a');
        }

        // Normal gameplay space press
        if (event.code === 'Space' && !isSpacePressedRef.current && !isGameOver && !isTransitioningRef.current) {
            isSpacePressedRef.current = true;
                        console.log("inside")

            // Resume after pause by errorOne/Two/Three
            if (isPausedRef.current) {
                                console.log("Resuming game...");
                setErrorOneMessage(false);
                setErrorTwoMessage(false);
                setErrorThreeMessage(false);
                missedResponsesRef.current = 0;
                isPausedRef.current = false;
                const randomDelay = (Math.random() * (config.delayBetweenWordsMax - config.delayBetweenWordsMin) + config.delayBetweenWordsMin);
                                console.log(`random delay ${randomDelay}`);
                setCurrentWord("");
                displayNextWordTimerRef.current = setTimeout(displayNextWord, randomDelay);
                return;
            }

            clearTimeout(timerRef.current); // cancel miss timeout

            const reactionTime = performance.now() - startTimeRef.current;
            const roundedReactionTime = Math.round(reactionTime * 100) / 100;

            // Error Two: Too fast
            if (reactionTime < config.minReactionTime) {
                                console.log(`Reaction time too fast: ${reactionTime}ms. Pausing game.`);
                setCurrentWord("");
                isPausedRef.current = true;
                missedResponsesRef.current = 0;
                isSpacePressedRef.current = false;
                setErrorTwoMessage(true);
                const currentWord = finalWordListRef.current[wordIndexRef.current];
                iatTestReaction.current.push([
                    currentWord,
                    roundedReactionTime,
                    memories[currentMemoryIndex.current],
                    gamesPlayed.current,
                    wordIndexRef.current,
                    true, // fast reaction recorded
                    currentMemoryIndex.current + 1,
                    questionID[0],
                ]);
                return;
            }

            // Record correct reaction
            const currentWord = finalWordListRef.current[wordIndexRef.current];
            iatTestReaction.current.push([
                currentWord,
                roundedReactionTime,
                memories[currentMemoryIndex.current],
                gamesPlayed.current,
                wordIndexRef.current,
                true,
                currentMemoryIndex.current + 1,
                questionID[0],
            ]);
            missedResponsesRef.current = 0;
                        console.log(`Reaction time for ${currentWord}: ${reactionTime}ms`);

            // Proceed to next word after random delay
            const randomDelay = (Math.random() * (config.delayBetweenWordsMax - config.delayBetweenWordsMin) + config.delayBetweenWordsMin);
                        console.log(`random delay ${randomDelay}`);
            isTransitioningRef.current = true;
            setCurrentWord("");
            displayNextWordTimerRef.current = setTimeout(displayNextWord, randomDelay);

        } else {
            // Error Three: Space pressed when no word (during transition)
            if (!gameEnded.current && event.code === 'Space') {
                                console.log("pressed on space while transitioning between attributes")
                clearTimeout(displayNextWordTimerRef.current);
                clearTimeout(timerRef.current);
                setCurrentWord("");
                isPausedRef.current = true;
                missedResponsesRef.current = 0;
                isSpacePressedRef.current = false;
                isTransitioningRef.current = false;
                setErrorThreeMessage(true);
            }
        }
    };

    /**
     * ──────────────────────────────────────────────
     *  Rendering — Game UI
     * ──────────────────────────────────────────────
     */
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 relative">
            {isGameOver ? (
                <div className="flex flex-col items-center justify-center">
                    {isBreak && <h2 className="text-4xl font-semibold mb-4">{translation("IAT_IntermissionText")}</h2>}
                    {isBreak1 && <h2 className="text-4xl font-semibold mb-4">{translation("IAT_NextImplicitMessage")}</h2>}
                    {canContinue && <h2 className="absolute bottom-20 text-4xl text-gray-800 mt-4">{translation("IAT_PressSpaceToContinue")}</h2>}
                    {canContinue3 && <h2 className="absolute bottom-20 text-4xl text-gray-800 mt-4">{translation("IAT_PressSpaceToContinue")}</h2>}
                                        {/* <h2 className="text-xl font-semibold mb-4">This text and below details are currently used for testing purpose, will be removed in the final version</h2>
                    <h2>Reaction Times:</h2>
                    <ul>
                    {iatTestReaction.current.map((result, index) => (
                        <li key={index}>
                            Word: {result[0]} <br />
                            Reaction Time: { `${result[1]}ms`} <br />
                            Memory: {result[2]} <br />
                            Games Played: {result[3]} <br />
                            Current Index: {result[4]} <br />
                            User Reacted: {result[5]}
                        </li>
                    ))}
                    </ul> */}
                </div>
            ) : (
                <div>
                    <div className="fixed inset-0 flex flex-col items-center justify-center">
                        {!errorOneMessage && !errorTwoMessage && !errorThreeMessage &&
                            <h2 className="absolute transform -translate-y-16 text-2xl font-semibold text-gray-500">
                                {prompts[0]}
                            </h2>}
                        <h2 className="absolute top-1/2 transform -translate-y-1/2 text-6xl">{currentWord}</h2>
                    </div>

                    {/* Error overlays with translations */}
                    {errorOneMessage && (
                        <div className="text-red-600 mb-8 text-left font-bold left-0 text-3xl">
                            {translation("IAT_NoResponsesDetected")}
                            <h6 className="text-2xl text-gray-700 mb-4 mt-4 text-left w-full max-w-5xl">
                                {translation("IAT_NoResponsesMessage")}
                                <br /> {translation("IAT_RememberToPressSpace")}
                                <br /> {translation("IAT_ResponsesMonitored")}
                                <br /> {translation("IAT_PleasePressSpaceToContinue")}
                            </h6>
                        </div>
                    )}

                    {errorTwoMessage && (
                        <div className="text-red-600 mb-8 text-left font-bold left-0 text-3xl">
                            {translation("IAT_TooFastWarning")}
                            <h6 className="text-2xl text-gray-700 mb-4 mt-4 text-left w-full max-w-5xl">
                                {translation("IAT_TooFastMessage")}
                                <br /> {translation("IAT_ConsiderEachWord")}
                                <br /> {translation("IAT_ResponsesMonitoredFocus")}
                                <br /> {translation("IAT_PleasePressSpaceToContinue")}
                            </h6>
                        </div>
                    )}

                    {errorThreeMessage && (
                        <div className="text-red-600 mb-8 text-left font-bold left-0 text-3xl">
                            {translation("IAT_NoWordOnScreenWarning")}
                            <h6 className="text-2xl text-gray-700 mb-4 mt-4 text-left w-full max-w-5xl">
                                {translation("IAT_NoWordMessage")}
                                <br /> {translation("IAT_RespondWhenWordAppears")}
                                <br /> {translation("IAT_ResponsesMonitoredFocusAgain")}
                                <br /> {translation("IAT_PleasePressSpaceToContinue")}
                            </h6>
                        </div>
                    )}
                </div>
            )}

            {/* Inactivity Overlay */}
            {isInactive && (
                <div className="fixed inset-0 flex items-center justify-center bg-yellow-200">
                    <p className="text-5xl font-bold">{kioskTimeoutText.current}</p>
                </div>
            )}
        </div>
    );
};

export default IATTestv2;